import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { editorialArticles } from '../data/mock';
import { ArrowRight } from 'lucide-react';

const EditorialCard = ({ article, featured = false, t }) => {
  // Get translated content
  const translatedContent = t(`editorial.articles.${article.id}`);
  const displayArticle = (translatedContent && typeof translatedContent === 'object') 
    ? {
        ...article,
        title: translatedContent.title || article.title,
        excerpt: translatedContent.excerpt || article.excerpt,
        category: translatedContent.category || article.category
      }
    : article;

  return (
    <Link
      to={`/editorial/${article.id}`}
      className={`group block relative overflow-hidden ${
        featured ? 'lg:col-span-2 lg:row-span-2' : ''
      }`}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={article.image}
          alt={displayArticle.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/60 to-transparent" />
      </div>

      {/* Content */}
      <div
        className={`relative z-10 h-full flex flex-col justify-end ${
          featured ? 'p-8 lg:p-12 min-h-[500px] lg:min-h-[600px]' : 'p-6 min-h-[300px]'
        }`}
      >
        {/* Category Badge */}
        <span className="inline-block self-start px-3 py-1 bg-[#52ea88] text-[#0B0F0E] text-xs font-semibold uppercase tracking-wider mb-4">
          {displayArticle.category}
        </span>

        {/* Title */}
        <h3
          className={`font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors leading-tight ${
            featured ? 'text-2xl lg:text-4xl' : 'text-xl'
          }`}
        >
          {displayArticle.title}
        </h3>

        {/* Excerpt */}
        <p
          className={`text-[#9FBFB4] mt-3 line-clamp-2 ${
            featured ? 'text-lg' : 'text-sm'
          }`}
        >
          {displayArticle.excerpt}
        </p>

        {/* Read More */}
        <div className="flex items-center gap-2 mt-4 text-[#52ea88] group-hover:gap-3 transition-all">
          <span className="text-sm font-medium">{t('editorial.readArticle')}</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>

      {/* Border Effect */}
      <div className="absolute inset-0 border border-transparent group-hover:border-[#52ea88]/30 transition-colors duration-400" />
    </Link>
  );
};

const Editorial = () => {
  const { t } = useLanguage();

  return (
    <section className="bg-[#0B0F0E] py-24 border-t border-[#52ea88]/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
          <div className="space-y-2">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#E6FFF6]">
              {t('sections.editorial')}
            </h2>
            <div className="w-20 h-1 bg-[#52ea88]"></div>
          </div>
          <Link
            to="/editorial"
            className="group flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors"
          >
            <span>{t('editorial.viewAll') || 'View All'}</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Editorial Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {editorialArticles.map((article, index) => (
            <EditorialCard
              key={article.id}
              article={article}
              featured={index === 0}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Editorial;
